import {
  Box,
  Stack,
  Tab,
  TabProps,
  Tabs as MuiTabs,
  TabsProps,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { ReactNode, Ref, useContext, useMemo, useState } from 'react';
import {
  DrawerStickyHeader,
  useDrawerStickyHeader,
} from 'components/DrawerStickyHeader';
import { MapEntityDrawerContext } from 'features/mapping/components/MapEntityDrawer';
import { MapEntityDrawerHeader } from 'features/mapping/components/MapEntityDrawerHeader';
import { MAP_ENTITY_DETAILS_LAYOUT_SPACING } from './MapEntityDetailsLayout.constants';
import {
  MapEntityDetailsLayoutContext,
  useMapEntityDetailsLayoutContext,
} from './MapEntityDetailsLayoutContext';

type RootProps = {
  children: ReactNode;
};

type HeaderProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  caption?: ReactNode;
  sticky?: boolean;
  hideSubtitleWhenSticky?: boolean;
};

type BodyProps = {
  children: ReactNode;
  pb?: number;
  gap?: number;
  sx?: SxProps<Theme>;
  /** Ref for the section element (used for scroll-position measurement). */
  containerRef?: Ref<HTMLDivElement>;
};

type MetaProps = {
  children: ReactNode;
  sx?: SxProps<Theme>;
  'data-testid'?: string;
};

type ActionsProps = {
  children: ReactNode;
  sx?: SxProps<Theme>;
};

type LayoutTabsProps = {
  tabsProps: TabsProps;
  tabProps: TabProps[];
  tabPanels: ReactNode;
  /**
   * Content rendered inside the sticky tab region, above the tabs, only while
   * the tab bar is fixed (e.g. wildfire/flooding "scroll to top" evac button).
   */
  topSlot?: ReactNode;
  /**
   * Passed through to `DrawerStickyHeader`; keeps the holder height stable when
   * a `topSlot` toggles the sticky region's height.
   */
  dontUpdateHolderHeightWhenSticky?: boolean;
};

type MapEntityDetailsLayoutComponent = {
  Root: (props: RootProps) => JSX.Element;
  Header: (props: HeaderProps) => JSX.Element;
  Body: (props: BodyProps) => JSX.Element;
  Meta: (props: MetaProps) => JSX.Element;
  Actions: (props: ActionsProps) => JSX.Element;
  Tabs: (props: LayoutTabsProps) => JSX.Element;
};

const { bodyPaddingX, bodyPaddingBottom, bodyGap, actionsSpacing, metaGap } =
  MAP_ENTITY_DETAILS_LAYOUT_SPACING;

const Root = (props: RootProps): JSX.Element => {
  const { children } = props;
  const { scrollElementRef } = useContext(MapEntityDrawerContext);
  const {
    isFixed,
    contentHeight,
    onStickyChange,
    onStickyContentHeightChange,
  } = useDrawerStickyHeader();

  const value = useMemo(
    () => ({
      scrollElementRef,
      headerContentHeight: contentHeight,
      isHeaderFixed: isFixed,
      onHeaderStickyChange: onStickyChange,
      onHeaderContentHeightChange: onStickyContentHeightChange,
    }),
    [
      scrollElementRef,
      contentHeight,
      isFixed,
      onStickyChange,
      onStickyContentHeightChange,
    ],
  );

  return (
    <MapEntityDetailsLayoutContext.Provider value={value}>
      <Box sx={{ mb: 'env(safe-area-inset-bottom)', width: '100%' }}>
        {children}
      </Box>
    </MapEntityDetailsLayoutContext.Provider>
  );
};

const Header = (props: HeaderProps): JSX.Element => {
  const {
    title,
    subtitle,
    caption,
    sticky = true,
    hideSubtitleWhenSticky = false,
  } = props;
  const {
    scrollElementRef,
    isHeaderFixed,
    onHeaderStickyChange,
    onHeaderContentHeightChange,
  } = useMapEntityDetailsLayoutContext();

  const showSubtitle =
    Boolean(subtitle) && (!hideSubtitleWhenSticky || !isHeaderFixed);

  const contentContainer = (
    <DrawerStickyHeader.ContentContainer>
      <Stack spacing={metaGap}>
        <MapEntityDrawerHeader>{title}</MapEntityDrawerHeader>

        {showSubtitle &&
          (typeof subtitle === 'string' ? (
            <Typography>{subtitle}</Typography>
          ) : (
            subtitle
          ))}

        {Boolean(caption) &&
          (typeof caption === 'string' ? (
            <Typography variant="body2" color="textSecondary">
              {caption}
            </Typography>
          ) : (
            caption
          ))}
      </Stack>
    </DrawerStickyHeader.ContentContainer>
  );

  if (!sticky) {
    return contentContainer;
  }

  return (
    <DrawerStickyHeader
      scrollContainer={scrollElementRef?.current as HTMLElement}
      onStickyChange={onHeaderStickyChange}
      onStickyContentHeightChange={onHeaderContentHeightChange}
    >
      {contentContainer}
    </DrawerStickyHeader>
  );
};

const Body = (props: BodyProps): JSX.Element => {
  const {
    children,
    pb = bodyPaddingBottom,
    gap = bodyGap,
    sx,
    containerRef,
  } = props;
  return (
    <Stack ref={containerRef} sx={{ px: bodyPaddingX, gap, pb, ...sx }}>
      {children}
    </Stack>
  );
};

const Meta = (props: MetaProps): JSX.Element => {
  const { children, sx, 'data-testid': dataTestId } = props;
  return (
    <Stack spacing={metaGap} sx={sx} data-testid={dataTestId}>
      {children}
    </Stack>
  );
};

const Actions = (props: ActionsProps): JSX.Element => {
  const { children, sx } = props;
  return (
    <Stack spacing={actionsSpacing} sx={sx}>
      {children}
    </Stack>
  );
};

const LayoutTabs = (props: LayoutTabsProps): JSX.Element => {
  const {
    tabsProps,
    tabProps,
    tabPanels,
    topSlot,
    dontUpdateHolderHeightWhenSticky,
  } = props;
  const { scrollElementRef, headerContentHeight } =
    useMapEntityDetailsLayoutContext();
  const [isFixed, setIsFixed] = useState(false);

  return (
    // Keep the sticky tab bar and its panels grouped as one unit so pages can't
    // misplace the panels (they always render inside Root, beneath the tabs).
    <Box>
      <DrawerStickyHeader
        scrollContainer={scrollElementRef?.current as HTMLElement}
        // Offset up by the header height so the tab bar docks directly beneath
        // the fixed header; marginTop reserves that space in the scroll flow,
        // pulled up 1px so scroll content can't bleed through the header seam.
        topOffset={headerContentHeight * -1}
        stickyStyle={{ marginTop: Math.max(0, headerContentHeight - 1) }}
        onStickyChange={setIsFixed}
        dontUpdateHolderHeightWhenSticky={dontUpdateHolderHeightWhenSticky}
      >
        {topSlot && isFixed ? topSlot : null}

        <MuiTabs {...tabsProps}>
          {tabProps.map((tab) => {
            // `key` must not be spread into JSX.
            const { key, ...rest } = tab;
            return <Tab key={key} {...rest} />;
          })}
        </MuiTabs>
      </DrawerStickyHeader>

      {/*
        Contain panel z-indexing in its own stacking context so panel content
        with high z-index (e.g. WeatherStationDataTable's sticky cells) can't
        paint over the sticky header/tab bar as it scrolls.
      */}
      <Box sx={{ isolation: 'isolate' }}>{tabPanels}</Box>
    </Box>
  );
};

export const MapEntityDetailsLayout: MapEntityDetailsLayoutComponent = {
  Root,
  Header,
  Body,
  Meta,
  Actions,
  Tabs: LayoutTabs,
};

export type {
  RootProps as MapEntityDetailsLayoutRootProps,
  HeaderProps as MapEntityDetailsLayoutHeaderProps,
  BodyProps as MapEntityDetailsLayoutBodyProps,
  LayoutTabsProps as MapEntityDetailsLayoutTabsProps,
};
